import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { BalanceVisibilityProvider } from './contexts/BalanceVisibilityContext.tsx';
import Layout from './components/Layout.tsx';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import ForgotPassword from './pages/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Accounts from './pages/Accounts.tsx';
import Categories from './pages/Categories.tsx';
import Transactions from './pages/Transactions.tsx';
import Tasks from './pages/Tasks.tsx';
import Settings from './pages/Settings.tsx';
import Profile from './pages/Profile.tsx';
import SetupBackendIP from './pages/SetupBackendIP.tsx';
import InvestmentPlan from './pages/InvestmentPlan.tsx';
import InvestmentTracking from './pages/InvestmentTracking.tsx';
import InvestmentScenarios from './pages/InvestmentScenarios.tsx';
import InvestmentAllocation from './pages/InvestmentAllocation.tsx';
import InvestmentGoals from './pages/InvestmentGoals.tsx';
import InvestmentHelp from './pages/InvestmentHelp.tsx';
import Objectives from './pages/Objectives.tsx';
import ObjectiveDetail from './pages/ObjectiveDetail.tsx';
import KeyResultDetail from './pages/KeyResultDetail.tsx';
import FIIPortfolio from './pages/FIIPortfolio.tsx';
import FIIOperations from './pages/FIIOperations.tsx';
import FIIIncome from './pages/FIIIncome.tsx';
import FIIDarf from './pages/FIIDarf.tsx';
import FIIDashboard from './pages/FIIDashboard.tsx';
import FIIFaq from './pages/FIIFaq.tsx';
import FIIAssetDeclaration from './pages/FIIAssetDeclaration.tsx';
import Import from './pages/Import.tsx';
import ImportRules from './pages/ImportRules.tsx';
import ItemizationRules from './pages/ItemizationRules.tsx';
import Reports from './pages/Reports.tsx';
import './App.css';

// Componente para rotas protegidas
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  console.log('[ProtectedRoute] Renderizando - loading:', loading, 'isAuthenticated:', isAuthenticated, 'user:', user);
  
  // Show loading while checking auth
  if (loading) {
    console.log('[ProtectedRoute] Mostrando loading...');
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Carregando...</div>
      </div>
    );
  }
  
  // Simple check: if authenticated, allow access
  if (isAuthenticated) {
    console.log('[ProtectedRoute] Usuário autenticado, permitindo acesso');
    return <>{children}</>;
  }
  
  // Not authenticated, redirect to login
  console.log('[ProtectedRoute] Usuário NÃO autenticado, redirecionando para login');
  return <Navigate to="/login" replace />;
};

// Componente principal da aplicação
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();

  return (
    <Router>
      <Routes>
        {/* Rotas públicas */}
        <Route 
          path="/login" 
          element={
            !loading && isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login />
            )
          } 
        />
        <Route 
          path="/register" 
          element={
            !loading && isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Register />
            )
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            !loading && isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ForgotPassword />
            )
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            !loading && isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ResetPassword />
            )
          } 
        />
        
        {/* Rotas protegidas */}
        <Route 
          path="/" 
          element={
            <Navigate 
              to={!loading && isAuthenticated ? "/dashboard" : "/login"} 
              replace 
            />
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/accounts" 
          element={
            <ProtectedRoute>
              <Layout>
                <Accounts />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/categories" 
          element={
            <ProtectedRoute>
              <Layout>
                <Categories />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/transactions" 
          element={
            <ProtectedRoute>
              <Layout>
                <Transactions />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <Layout>
                <Tasks />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <Layout>
                <Reports />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/investment-plan" 
          element={
            <ProtectedRoute>
              <Layout>
                <InvestmentPlan />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/investment-help" 
          element={
            <ProtectedRoute>
              <Layout>
                <InvestmentHelp />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/investment-tracking" 
          element={
            <ProtectedRoute>
              <Layout>
                <InvestmentTracking />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/investment-scenarios" 
          element={
            <ProtectedRoute>
              <Layout>
                <InvestmentScenarios />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/investment-allocation" 
          element={
            <ProtectedRoute>
              <Layout>
                <InvestmentAllocation />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/investment-goals" 
          element={
            <ProtectedRoute>
              <Layout>
                <InvestmentGoals />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/objectives" 
          element={
            <ProtectedRoute>
              <Layout>
                <Objectives />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/objectives/:id" 
          element={
            <ProtectedRoute>
              <Layout>
                <ObjectiveDetail />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/key-results/:id" 
          element={
            <ProtectedRoute>
              <Layout>
                <KeyResultDetail />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/setup-backend-ip" 
          element={<SetupBackendIP />}
        />

        {/* FII Module routes */}
        <Route
          path="/fii/portfolio"
          element={
            <ProtectedRoute>
              <Layout>
                <FIIPortfolio />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fii/operations"
          element={
            <ProtectedRoute>
              <Layout>
                <FIIOperations />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fii/income"
          element={
            <ProtectedRoute>
              <Layout>
                <FIIIncome />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fii/darf"
          element={
            <ProtectedRoute>
              <Layout>
                <FIIDarf />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fii/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <FIIDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fii/faq"
          element={
            <ProtectedRoute>
              <Layout>
                <FIIFaq />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fii/declaracao-bens"
          element={
            <ProtectedRoute>
              <Layout>
                <FIIAssetDeclaration />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Import Module routes */}
        <Route
          path="/import"
          element={
            <ProtectedRoute>
              <Layout>
                <Import />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/import/rules"
          element={
            <ProtectedRoute>
              <Layout>
                <ImportRules />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/itemization/rules"
          element={
            <ProtectedRoute>
              <Layout>
                <ItemizationRules />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <BalanceVisibilityProvider>
        <AppContent />
      </BalanceVisibilityProvider>
    </AuthProvider>
  );
}

export default App;